import React from 'react';
import { Modal, Field } from '../../components/ui';
import { INVITE_STATUSES, VISIBILITIES, normaliseText } from '../../utils/helpers';
import { MAX_FAMILY_SIZE } from './useMembers';

const ten = (v) => v.replace(/\D/g, '').slice(0, 10);

// Shared add/edit modal — identical on desktop and mobile (the Modal is already
// responsive). All state/handlers come from the useMembers hook (vm).
// Desktop guest form: wide, dense 4-column modal so the whole form fits one screen.
export default function MemberFormDesktop({ vm }) {
  const {
    functions, form, setF, setMobile, editing, fnName, me, selectedFunctionIds, toggleFunction,
    party, addParty, setPartyField, removeParty, familyMode,
    addMode, setAddMode, distinctPeople, dupes, blockedSameFn,
    canImport, importContact, pickExisting, save, saving, setOpen,
    clearForm, isDirty, isMarriage, sideOptions,
  } = vm;

  return (
    <Modal
      wide dense
      title={editing ? 'Edit Guest' : (familyMode ? 'Add Family' : 'Add Guest')}
      onClose={() => setOpen(false)}
      isDirty={editing ? undefined : isDirty}
      onClear={clearForm}
      footer={<>
        <button className="btn light" onClick={() => setOpen(false)}>Cancel</button>
        <button className="btn primary" disabled={saving || blockedSameFn} onClick={save}>
          {saving ? 'Saving...' : (editing ? 'Save' : (familyMode ? 'Add Family' : 'Add Guest'))}
        </button>
      </>}
    >
      {!editing && !familyMode && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <button className={`btn sm ${addMode === 'new' ? 'primary' : 'light'}`} onClick={() => setAddMode('new')}>New person</button>
          <button className={`btn sm ${addMode === 'existing' ? 'primary' : 'light'}`}
            onClick={() => setAddMode('existing')} disabled={!distinctPeople.length}>Pick existing</button>
          {canImport && (
            <button className="btn sm light" onClick={importContact} title="Pick a contact from your phone book">
              📇 From Contacts
            </button>
          )}
        </div>
      )}

      {!editing && !familyMode && addMode === 'existing' && (
        <Field label="Existing member (re-invite to another function)" type="select" value=""
          options={distinctPeople.map(p => ({ value: p.GuestId, label: `${[p.Surname, p.GuestName, p.FatherHusband].filter(Boolean).join(' ')} · ${p.MobileNumber || ''}` }))}
          onChange={pickExisting} />
      )}

      <div className="form-grid cols-4">
        <div className="span-all">
          <label className="field">
            <span>Functions <b>*</b></span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', border: '1px solid var(--line)', borderRadius: 10, padding: '8px 10px' }}>
              {functions.map(f => (
                <label key={f.FunctionId} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontWeight: 500, color: 'var(--ink)' }}>
                  <input
                    type="checkbox"
                    checked={selectedFunctionIds.includes(String(f.FunctionId))}
                    onChange={() => toggleFunction(f.FunctionId)}
                    style={{ width: 'auto' }}
                  />
                  <span>{f.FunctionName}</span>
                </label>
              ))}
            </div>
          </label>
        </div>
        <Field label={familyMode ? 'Family Head Surname' : 'Surname'} value={form.Surname} onChange={(v) => setF('Surname', normaliseText(v))} />
        <Field label={familyMode ? 'Family Head Name' : 'Name'} value={form.GuestName} required onChange={(v) => setF('GuestName', normaliseText(v))} />
        {!familyMode && <Field label="Father / Husband Name" value={form.FatherHusband} onChange={(v) => setF('FatherHusband', normaliseText(v))} />}
        <Field label={familyMode ? 'Family Head Phone Number' : 'Mobile Number'} value={form.MobileNumber} required onChange={setMobile} />
      </div>

      {!editing && dupes.length > 0 && (
        <div className="card card-pad" style={{ marginTop: 10, background: blockedSameFn ? '#fee2e2' : '#fef3c7', fontSize: 13 }}>
          <strong>Already added in this event:</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {dupes.map(d => (
              <li key={d.GuestId}>
                {(d.InvitedByName || '').trim() || 'Someone'} - {d.FunctionName || fnName(d.FunctionId)}
                {String(d.CreatedBy) === String(me) ? ' (you)' : ''}
              </li>
            ))}
          </ul>
          {blockedSameFn
            ? <div style={{ marginTop: 6, color: 'var(--danger)' }}>You already added this member to this function. Change the function to add them elsewhere.</div>
            : <div style={{ marginTop: 6 }}>The same person can be invited by more than one member. You can still add them to your own list.</div>}
        </div>
      )}

      {!editing && familyMode && <div style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <strong style={{ fontSize: 13 }}>Other Family Members</strong>
          <span className="muted" style={{ fontSize: 12 }}>{party.length + 1} people including the head</span>
          <div style={{ flex: 1 }} />
          <button type="button" className="btn sm light" onClick={addParty} disabled={party.length >= MAX_FAMILY_SIZE - 1}>+ Add Member</button>
        </div>
        {party.map((m, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            <span className="muted" style={{ fontSize: 12, width: 18 }}>{i + 2}.</span>
            <input placeholder="Surname" value={m.Surname}
              onChange={e => setPartyField(i, 'Surname', normaliseText(e.target.value))}
              style={{ flex: '1 1 90px', padding: '6px 10px', border: '1px solid var(--line)', borderRadius: 8 }} />
            <input placeholder="Name" value={m.GuestName}
              onChange={e => setPartyField(i, 'GuestName', normaliseText(e.target.value))}
              style={{ flex: '1 1 90px', padding: '6px 10px', border: '1px solid var(--line)', borderRadius: 8 }} />
            <input placeholder="Phone Number" value={m.MobileNumber}
              onChange={e => setPartyField(i, 'MobileNumber', ten(e.target.value))}
              style={{ flex: '1 1 110px', padding: '6px 10px', border: '1px solid var(--line)', borderRadius: 8 }} />
            <button type="button" className="btn danger sm" onClick={() => removeParty(i)}>Remove</button>
          </div>
        ))}
      </div>}

      <div className="form-grid cols-4" style={{ marginTop: 6 }}>
        <Field label="Visibility" type="select" value={form.Visibility} options={VISIBILITIES} onChange={(v) => setF('Visibility', v)} />
        {isMarriage && <Field label="Side" type="select" value={form.Side} options={sideOptions} onChange={(v) => setF('Side', v)} />}
        {editing && (
          <>
            <Field label="Relation" value={form.Relation} onChange={(v) => setF('Relation', normaliseText(v))} />
            <Field label="Group" value={form.GroupName} onChange={(v) => setF('GroupName', normaliseText(v))} />
            <Field label="Invite Status" type="select" value={form.InviteStatus} options={INVITE_STATUSES} onChange={(v) => setF('InviteStatus', v)} />
            <Field label="Invited Via" type="select" value={form.InvitedVia} options={['WhatsApp', 'Call', 'Card']} onChange={(v) => setF('InvitedVia', v)} />
          </>
        )}
        <div className="span-all">
          <Field label="Remarks" type="textarea" rows={2} value={form.Remarks} onChange={(v) => setF('Remarks', v)} />
        </div>
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
        <strong>Visibility:</strong> Private = only you (and Event Admin) can see this guest · Common = all members can see it.
      </div>
    </Modal>
  );
}
